import { Box, Card, CardContent, CardActionArea, Typography, Skeleton, Pagination } from '@mui/material';
import InboxIcon from '@mui/icons-material/Inbox';

/**
 * Mobile card-based list view — replaces DataGrid on phones.
 *
 * Props:
 *   rows: Array of data objects
 *   cardRenderer: (row) => JSX — defines how each card looks
 *   onRowClick: (row) => void — tap handler
 *   loading: boolean
 *   getRowId: (row) => string|number — extract row ID
 *   emptyMessage: string — shown when no rows
 *   page: number (1-based) — current page for server-side pagination
 *   pageSize: number — items per page
 *   totalRows: number — total count for pagination
 *   onPageChange: (page) => void — pagination handler
 */
const MobileCardList = ({
  rows = [],
  cardRenderer,
  onRowClick,
  loading = false,
  getRowId = (row) => row.id,
  emptyMessage = 'אין נתונים להצגה',
  page,
  pageSize = 25,
  totalRows,
  onPageChange,
}) => {
  if (loading) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        {[...Array(5)].map((_, i) => (
          <Card key={i} sx={{ borderRadius: 2 }}>
            <CardContent sx={{ p: 2 }}>
              <Skeleton variant="text" width="60%" height={24} />
              <Skeleton variant="text" width="40%" height={20} sx={{ mt: 0.5 }} />
              <Skeleton variant="text" width="50%" height={20} sx={{ mt: 0.5 }} />
            </CardContent>
          </Card>
        ))}
      </Box>
    );
  }

  if (!rows.length) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          py: 6,
          color: 'text.secondary',
        }}
      >
        <InboxIcon sx={{ fontSize: 48, mb: 1, opacity: 0.5 }} />
        <Typography variant="body1">{emptyMessage}</Typography>
      </Box>
    );
  }

  const totalPages = totalRows ? Math.ceil(totalRows / pageSize) : null;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      {rows.map((row) => {
        const rowId = getRowId(row);
        const cardContent = cardRenderer(row);

        if (onRowClick) {
          return (
            <Card key={rowId} sx={{ borderRadius: 2, overflow: 'hidden' }}>
              <CardActionArea onClick={() => onRowClick(row)}>
                <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                  {cardContent}
                </CardContent>
              </CardActionArea>
            </Card>
          );
        }

        return (
          <Card key={rowId} sx={{ borderRadius: 2 }}>
            <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
              {cardContent}
            </CardContent>
          </Card>
        );
      })}

      {/* Pagination */}
      {totalPages && totalPages > 1 && onPageChange && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
          <Pagination
            count={totalPages}
            page={page || 1}
            onChange={(e, newPage) => onPageChange(newPage)}
            color="primary"
            size="medium"
            sx={{
              '& .MuiPaginationItem-root': {
                minWidth: 36,
                minHeight: 36,
              },
            }}
          />
        </Box>
      )}
    </Box>
  );
};

export default MobileCardList;
